/**
 * Contact-editing interface.
 *
 * Add, edit and remove the contacts a profile reveals. The profile is identified
 * by an id passed in memory through navigation state, never in the URL — so a
 * page reload loses the selection and returns to the dashboard, which is
 * acceptable for the prototype and keeps ids out of the address bar.
 *
 * Names and telephone numbers are the sensitive payload. They are shown for
 * editing, but never placed in the URL, written to storage, or logged.
 */

import { el, icon, fieldValue } from "../ui/dom.js";
import { pageHeader, errorAlert, section } from "../ui/patterns.js";

/**
 * Turn a rejected service call into something the person can read.
 *
 * Every profile service call can be refused by the server — a plan limit, an
 * unverified email, a dropped connection — and the messages it returns are
 * already plain-language and safe to display (§9). Before this existed the
 * rejections became unhandled promise rejections: the server correctly refused a
 * fourth contact on the free plan, and the page showed nothing at all.
 *
 * @param {unknown} error
 * @returns {string}
 */
function messageFor(error) {
  return error instanceof Error && error.message
    ? error.message
    : "That did not work. Please try again.";
}

/** @type {import("../app/router.js").PageRenderer} */
export default function renderEditContacts(context) {
  const frag = document.createDocumentFragment();
  const profileId = typeof context.state.profileId === "string" ? context.state.profileId : null;

  if (!profileId) {
    frag.append(
      pageHeader("Edit contacts"),
      errorAlert("Choose a profile from your dashboard to edit its contacts."),
      backToDashboard(context),
    );
    return frag;
  }

  const heading = pageHeader("Edit contacts");
  frag.append(heading);

  const body = el("div", { class: "kin-stack" }, [el("wa-skeleton", { effect: "sheen" })]);
  frag.append(body);

  const reload = async () => {
    /** @type {import("../types/models.js").RecoveryProfile | null} */
    let profile;
    try {
      profile = await context.services.profiles.get(profileId);
    } catch (error) {
      body.replaceChildren(errorAlert(messageFor(error)), backToDashboard(context));
      return;
    }

    if (!profile) {
      body.replaceChildren(
        errorAlert("That profile could not be found."),
        backToDashboard(context),
      );
      return;
    }

    const used = profile.contacts.length;
    const allowance = profile.maxContacts;
    const atLimit = typeof allowance === "number" && used >= allowance;

    body.replaceChildren(
      el("p", { class: "kin-lead" }, [`Contacts for “${profile.label}”.`]),
      // Show the allowance up front rather than only on rejection. The server
      // still enforces it; this just means nobody fills in a form that cannot
      // succeed.
      typeof allowance === "number"
        ? el("p", { class: "kin-note" }, [
            `${used} of ${allowance} contact${allowance === 1 ? "" : "s"} used on your plan.`,
          ])
        : el("span", { hidden: true }),
      renderEditableList(context, profile, reload),
      atLimit
        ? section("Add a contact", [
            el("p", { class: "kin-prose" }, [
              `You have used all ${allowance} contacts your plan allows in this profile. ` +
                "Remove one to add another, or upgrade for more.",
            ]),
            el(
              "wa-button",
              { appearance: "outlined", on: { click: () => context.navigate("/pricing") } },
              [icon("credit-card"), "See plans"],
            ),
          ])
        : renderAddForm(context, profile.id, reload),
      backToDashboard(context),
    );
  };

  reload();
  return frag;
}

/**
 * @param {import("../app/router.js").RouteContext} context
 * @param {import("../types/models.js").RecoveryProfile} profile
 * @param {() => void} reload
 * @returns {HTMLElement}
 */
function renderEditableList(context, profile, reload) {
  if (profile.contacts.length === 0) {
    return el("p", { class: "kin-note" }, ["No contacts yet. Add the first one below."]);
  }

  // Removal failures need somewhere to go — the button lives inside the list, so
  // the message belongs alongside it rather than in the add form below.
  const errorRegion = el("div", { "aria-live": "assertive" });

  const list = el(
    "ul",
    { class: "kin-edit-list" },
    profile.contacts.map((contact) =>
      el("li", {}, [
        el("wa-card", {}, [
          el("div", { class: "kin-contact-head" }, [
            el("span", { class: "kin-contact-name" }, [contact.name]),
            contact.relationship
              ? el("wa-badge", { variant: "neutral" }, [contact.relationship])
              : null,
            el("span", { class: "kin-note" }, [contact.telephone]),
          ]),
          el("div", { slot: "footer", class: "kin-contact-actions" }, [
            el(
              "wa-button",
              {
                appearance: "outlined",
                // "small" is deprecated in Web Awesome 3; the short form is
                // current and avoids a console warning on every render.
                size: "s",
                on: {
                  click: () => openContactDialog(context, profile.id, contact, reload),
                },
              },
              [icon("pen"), "Edit"],
            ),
            el(
              "wa-button",
              {
                appearance: "outlined",
                variant: "danger",
                size: "s",
                on: {
                  click: async () => {
                    errorRegion.replaceChildren();
                    try {
                      await context.services.profiles.removeContact(profile.id, contact.id);
                    } catch (error) {
                      errorRegion.replaceChildren(errorAlert(messageFor(error)));
                      return;
                    }
                    reload();
                  },
                },
              },
              [icon("trash"), "Remove"],
            ),
          ]),
        ]),
      ]),
    ),
  );

  return el("div", { class: "kin-stack" }, [list, errorRegion]);
}

/**
 * The inline add-contact form.
 *
 * @param {import("../app/router.js").RouteContext} context
 * @param {string} profileId
 * @param {() => void} reload
 * @returns {HTMLElement}
 */
function renderAddForm(context, profileId, reload) {
  const nameField = el("wa-input", { label: "Name", name: "name", required: true });
  const telField = el("wa-input", {
    label: "Telephone number",
    name: "telephone",
    type: "tel",
    inputmode: "tel",
    required: true,
  });
  const relField = el("wa-input", { label: "Relationship (optional)", name: "relationship" });
  const errorRegion = el("div", { "aria-live": "assertive" });

  const form = el(
    "form",
    {
      class: "kin-stack kin-form",
      novalidate: true,
      on: {
        submit: async (event) => {
          event.preventDefault();
          errorRegion.replaceChildren();
          const name = fieldValue(nameField);
          const telephone = fieldValue(telField);
          if (!name || !telephone) {
            errorRegion.replaceChildren(errorAlert("Enter both a name and a telephone number."));
            return;
          }
          const relationship = fieldValue(relField);
          try {
            await context.services.profiles.addContact(profileId, {
              name,
              telephone,
              ...(relationship ? { relationship } : {}),
            });
          } catch (error) {
            // Most often a plan limit or a telephone number the server could not
            // normalise. Both carry a message worth reading, and the form keeps
            // its values so nothing typed is lost.
            errorRegion.replaceChildren(errorAlert(messageFor(error)));
            return;
          }
          reload();
        },
      },
    },
    [
      nameField,
      telField,
      relField,
      errorRegion,
      el("wa-button", { type: "submit", variant: "brand" }, [icon("plus"), "Add contact"]),
    ],
  );

  return section("Add a contact", [form]);
}

/**
 * Edit an existing contact in a dialog.
 *
 * @param {import("../app/router.js").RouteContext} context
 * @param {string} profileId
 * @param {import("../types/models.js").Contact} contact
 * @param {() => void} reload
 * @returns {void}
 */
function openContactDialog(context, profileId, contact, reload) {
  const nameField = el("wa-input", { label: "Name", value: contact.name, required: true });
  const telField = el("wa-input", {
    label: "Telephone number",
    value: contact.telephone,
    type: "tel",
    required: true,
  });
  const relField = el("wa-input", {
    label: "Relationship (optional)",
    value: contact.relationship ?? "",
  });

  const errorRegion = el("div", { "aria-live": "assertive" });

  const dialog = el("wa-dialog", { label: "Edit contact" }, [
    el("div", { class: "kin-stack" }, [nameField, telField, relField, errorRegion]),
    el(
      "wa-button",
      { slot: "footer", appearance: "outlined", on: { click: () => dialog.remove() } },
      ["Cancel"],
    ),
    el(
      "wa-button",
      {
        slot: "footer",
        variant: "brand",
        on: {
          click: async () => {
            errorRegion.replaceChildren();
            const relationship = fieldValue(relField);
            try {
              await context.services.profiles.updateContact(profileId, {
                id: contact.id,
                name: fieldValue(nameField),
                telephone: fieldValue(telField),
                ...(relationship ? { relationship } : {}),
              });
            } catch (error) {
              // Kept open on failure. Closing the dialog would discard the edit
              // along with any explanation of why it was refused.
              errorRegion.replaceChildren(errorAlert(messageFor(error)));
              return;
            }
            dialog.remove();
            reload();
          },
        },
      },
      ["Save changes"],
    ),
  ]);

  document.body.append(dialog);
  // @ts-expect-error wa-dialog exposes an `open` property at runtime.
  dialog.open = true;
}

/**
 * @param {import("../app/router.js").RouteContext} context
 * @returns {HTMLElement}
 */
function backToDashboard(context) {
  return el(
    "wa-button",
    { appearance: "plain", on: { click: () => context.navigate("/dashboard") } },
    [icon("arrow-right"), "Back to your profiles"],
  );
}
